import React, { useState, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';

import Button from '../../misc/Button';
import Icon from '../../misc/Icon';

import InputSettingsField from './InputSettingsField';
import CheckboxSettingsField from './CheckboxSettingsField';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';

import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import DecoratorSettings from './DecoratorSettings';

import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import {
  StyledFieldWrapper,
  StyledButtonsWrapper,
  DividerLine,
} from './SettingsCommonElements';

import FieldTypes from '../../../constants/FieldTypes';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import {
  isSettingHidden,
  isStyleSettingHidden,
  getMetadataOptions,
} from '../../../lib/settingsHelper';
import { validateReadableId } from '@builttocreate/engine-utils';

const StyledWrapper = styled.div`
	padding: 12px 0px;
	display: flex;
	flex-direction: column;
`;

const StyledFieldWrapperRow = styled.div`
  display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	margin: 0px 12px 12px 12px;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

const FieldSettingsImageField = ({
  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,

  fieldId,
  fieldIdentifier,
  fieldPositionId,
  fieldSection,

  view,
  title,
  metadata,
  config,
  type,
  multi,
  required,
  hidden,
  hiddenViews,
  disabled,

  x,
  y,
  width,
  height,

  //Title styles
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //API Developers
  identifier,

  titleDisplay,
  borderColor,
  borderRadius,
  borderWidth,
  padding,
  margin,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onDuplicate,

  primaryMaxHeight,
  primaryMaxWidth,

  tipTitle,
  tipDescription,
  tipVisible,
  useReadableIds,

  decorators,
  onDecoratorCreate,
  onDecoratorChange,
  onDecoratorDelete,
}) => {
  const theme = useContext(ThemeContext);
  const [customImageWidthEnabled, setCustomImageWidthEnabled] = useState(
    !!primaryMaxWidth || !!primaryMaxHeight
  );
  const [readableIdError, setReadableIdError] = useState('');
  const prevProps = useRef();



  useEffect(() => {

    setCustomImageWidthEnabled(!!primaryMaxWidth || !!primaryMaxHeight);
    if (prevProps.current && prevProps.current !== fieldId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = fieldId;
  }, [fieldId]);

  const isTitleHidden = isSettingHidden(config, 'title');
  const isStylesHidden = isSettingHidden(config, 'styles');
  const isDisabledHidden = isSettingHidden(config, 'disabled');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isMultiHidden = isSettingHidden(config, 'multi');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isVisibilityHidden = isSettingHidden(config, 'hiddenViews');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isTooltipHidden = isSettingHidden(config, 'tooltip');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isUploadHidden = isSettingHidden(config, 'upload');
  const isIdHidden = isSettingHidden(config, '_id');
  const isDecoratorsHidden = isSettingHidden(config, 'decorators');

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(
    config,
    'titleFontWeight'
  );
  const isTitleTextTransformHidden = isStyleSettingHidden(
    config,
    'titleTextTransform'
  );
  const isTitleTextDecorationHidden = isStyleSettingHidden(
    config,
    'titleTextDecoration'
  );

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(
    config,
    'backgroundColor'
  );
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isHeaderOrFooter = fieldSection === 'header' || fieldSection === 'footer';

  const metadataOptions = getMetadataOptions(config);

  const stylesNode = (
    <>
      {isTitleFontSizeHidden &&
        isTitleFontColorHidden &&
        isTitleTextAlignHidden &&
        isTitleFontStyleHidden &&
        isTitleFontWeightHidden &&
        isTitleTextTransformHidden &&
        isTitleTextDecorationHidden ? null : (
        <>
          <AccordionSettings isOpen title="Title" height={170}>
            <FieldStyleSettings
              id={`${fieldId}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontSize: value,
                  }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontColor: value,
                  }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleTextAlign: value,
                  }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontStyle: value,
                  }),

                fontWeight: titleFontWeight,
                onFontWeightChange: isTitleFontWeightHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontWeight: value,
                  }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleTextTransform: value,
                  }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleTextDecoration: value,
                  }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {isBorderColorHidden &&
        isBorderRadiusHidden &&
        isBorderWidthHidden &&
        isBackgroundColorHidden &&
        isMarginHidden &&
        isPaddingHidden &&
        isPositionHidden ? null : (
        <AccordionSettings isOpen title="Field" height={400}>
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{
              borderColor,
              onBorderColorChange: isBorderColorHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  borderColor: value,
                }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  borderRadius: value,
                }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  borderWidth: value,
                }),

              padding,
              onPaddingChange: isPaddingHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  padding: value,
                }),

              margin,
              onMarginChange: isMarginHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  margin: value,
                }),

              zIndex,
              onZIndexChange: isZIndexHidden
                ? null
                : (value) => (value
                  ? onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    zIndex: value,
                  })
                  : ''),
            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  [field]: value,
                }),
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  /**
   * Logic below allows any file type for the file field
   *
   * Temporarily commented out as we have not built the upload functionality via field settings yet
  let acceptedFileTypes = {};

  if (type === FieldTypes.image) {

    // Logic below only allows images for the image field
    acceptedFileTypes = {
      'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
      'image/png': ['.png', '.PNG'],
    };

  }
  */

  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        type="text"
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid: Can't be duplicate or start with numbers, or contain spaces.`);

          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );


  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <TitleSettings
            {...{
              title,
              titleDisplay,
            }}
            onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
          />
        </>
      )}

      {isMultiHidden &&
        isUploadHidden &&
        isRequiredHidden &&
        isDisabledHidden ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          {isMultiHidden ? null : (
            <StyledFieldWrapper>
              <CheckboxSettingsField
                id="multi"
                icon={ThemeIcons.settings.multiImages}
                iconColor={multi ? theme.primaryColor : theme.borderColor}
                label={
                  type === FieldTypes.file ? 'Multiple Files' : 'Multiple Images'
                }
                selected={multi}
                onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  multi: selected,
                })}
              />
            </StyledFieldWrapper>
          )}
          {!multi || type === FieldTypes.file ? null : (
            <>
              <StyledFieldWrapper style={{ marginBottom: 0 }}>
                <ButtonGroupSettings
                  label="Image Size"
                  types={[
                    {
                      type: 'auto',
                      label: 'Auto',
                      active: !customImageWidthEnabled,
                    },
                    {
                      type: 'custom',
                      label: 'Custom',
                      active: customImageWidthEnabled,
                    },
                  ]}
                  onChange={(value) => {
                    if (value === 'custom') {
                      setCustomImageWidthEnabled(true);
                    } else if (value === 'auto') {
                      setCustomImageWidthEnabled(false);
                      onChange(fieldId, fieldIdentifier, fieldPositionId, {
                        primaryMaxWidth: 0,
                        primaryMaxHeight: 0,
                      });
                    }
                  }}
                />
              </StyledFieldWrapper>
              {!customImageWidthEnabled ? null : (
                <>
                  <StyledFieldWrapperRow>
                    <StyledFieldWrapper
                      style={{
                        maxWidth: '50%',
                        paddingRight: '4px',
                        margin: '0px',
                      }}
                    >
                      <InputSettingsField
                        value={primaryMaxWidth}
                        addonBefore={
                          <Icon icon={ThemeIcons.settings.primaryWidth} size="sm" />
                        }
                        type="number"
                        onChange={(i, value) => {
                          onChange(fieldId, fieldIdentifier, fieldPositionId, {
                            primaryMaxWidth: value ? parseFloat(value, 10) : 0,
                          });
                        }}
                      />
                    </StyledFieldWrapper>
                    <StyledFieldWrapper
                      style={{ maxWidth: '50%', paddingLeft: '4px', margin: '0px' }}
                    >
                      <InputSettingsField
                        value={primaryMaxHeight}
                        addonBefore={(
                          <Icon
                            icon={ThemeIcons.settings.primaryHeight}
                            size="sm"
                          />
                        )}
                        type="number"
                        onChange={(i, value) => {
                          onChange(fieldId, fieldIdentifier, fieldPositionId, {
                            primaryMaxHeight: value ? parseFloat(value, 10) : 0,
                          });
                        }}
                      />
                    </StyledFieldWrapper>
                  </StyledFieldWrapperRow>
                </>
              )}
            </>
          )}
          {isDisabledHidden ? null : (
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.disabled}
                iconColor={disabled ? theme.primaryColor : theme.borderColor}
                id="disabled"
                label="Read-only"
                selected={disabled}
                onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  disabled: selected,
                })}
              />
            </StyledFieldWrapper>
          )}
          {isRequiredHidden ? null : (
            !isHeaderOrFooter && (
              <StyledFieldWrapper>
                <CheckboxSettingsField
                  icon={ThemeIcons.settings.required}
                  iconColor={required ? theme.dangerColor : theme.borderColor}
                  iconSize="1x"
                  id="required"
                  label="Required"
                  selected={required}
                  onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    required: selected,
                  })}
                />
              </StyledFieldWrapper>
            )
          )}
          {(isHiddenHidden && (isLogicHidden || fields.length < 2)) ? null : (
            !isHeaderOrFooter && (
              <>
                <SettingsLabel>Conditional Logic</SettingsLabel>
                <DividerLine style={{ marginBottom: 14 }} />
                {isLogicHidden || fields.length < 2 ? null : (
                  <StyledFieldWrapper>
                    <ConditionalLogicSettings
                      fileId={fileId}
                      targetLabel=" field"
                      targetFieldId={fieldId}
                      sortedPages={sortedPages}
                      fields={fields}
                      logic={logic}
                      onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                        logic: nextLogic,
                      })}
                    />
                  </StyledFieldWrapper>
                )}
                {isHiddenHidden ? null : (
                  <StyledFieldWrapper>
                    <CheckboxSettingsField
                      icon={ThemeIcons.settings.hide}
                      iconColor={hidden ? theme.warningColor : theme.borderColor}
                      id="hidden"
                      label="Hidden"
                      selected={hidden}
                      onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                        hidden: selected,
                      })}
                    />
                  </StyledFieldWrapper>
                )}
              </>
            )
          )}
          {isVisibilityHidden ? null : (
            !isHeaderOrFooter && (
              <>
                <SettingsLabel>Visibility</SettingsLabel>
                <DividerLine style={{ marginBottom: 14 }} />
                <StyledFieldWrapper>
                  <CheckboxSettingsField
                    icon={ThemeIcons.settings.hide}
                    iconColor={hiddenViews?.includes('desktop') ? theme.warningColor : theme.borderColor}
                    id="hiddenViewsDesktop"
                    label="Hide on desktop"
                    selected={hiddenViews?.includes('desktop') || false}
                    onChange={(selected) => {
                      const next = selected
                        ? [...(hiddenViews || []), 'desktop']
                        : (hiddenViews || []).filter(v => v !== 'desktop');
                      onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                    }}
                  />
                </StyledFieldWrapper>
                <StyledFieldWrapper>
                  <CheckboxSettingsField
                    icon={ThemeIcons.settings.hide}
                    iconColor={hiddenViews?.includes('mobile') ? theme.warningColor : theme.borderColor}
                    id="hiddenViewsMobile"
                    label="Hide on mobile"
                    selected={hiddenViews?.includes('mobile') || false}
                    onChange={(selected) => {
                      const next = selected
                        ? [...(hiddenViews || []), 'mobile']
                        : (hiddenViews || []).filter(v => v !== 'mobile');
                      onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                    }}
                  />
                </StyledFieldWrapper>
                <StyledFieldWrapper>
                  <CheckboxSettingsField
                    icon={ThemeIcons.settings.hide}
                    iconColor={hiddenViews?.includes('pdf') ? theme.warningColor : theme.borderColor}
                    id="hiddenViewsPdf"
                    label="Hide on pdf"
                    selected={hiddenViews?.includes('pdf') || false}
                    onChange={(selected) => {
                      const next = selected
                        ? [...(hiddenViews || []), 'pdf']
                        : (hiddenViews || []).filter(v => v !== 'pdf');
                      onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                    }}
                  />
                </StyledFieldWrapper>
              </>
            )
          )}
          {isDecoratorsHidden ? null : (
            <>
              <SettingsLabel>Decorators</SettingsLabel>
              <DividerLine style={{ marginBottom: 14 }} />
              <DecoratorSettings
                decorators={decorators}
                onCreate={(data) => onDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
                onChange={(decoratorId, data) => onDecoratorChange(fieldId, fieldIdentifier, fieldPositionId, decoratorId, data)}
                onDelete={(decoratorId) => onDecoratorDelete(fieldId, fieldIdentifier, fieldPositionId, decoratorId)}
              />
            </>
          )}
        </>
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata
                ? { ...metadata, ...changes }
                : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                metadata: nextMetadata,
              });
            }}
          />
        </>
      )}

      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}

      {isTooltipHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings title="Tooltip" height={166}>
            <TooltipSettings
              {...{
                tipVisible,
                tipTitle,
                tipDescription,
              }}
              onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
            />
          </AccordionSettings>
        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                identifier: value,
              })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />

            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => onDuplicate(fieldId, fieldPositionId)}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={
          type === FieldTypes.file
            ? ThemeIcons.fields.file
            : ThemeIcons.fields.image
        }
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );
};

export default FieldSettingsImageField;
